import { useEffect } from 'react';

import { colors, typography } from '@pray/shared/components/foundations';
import HubspotForm from '@pray/shared/components/HubspotForm/HubspotForm';
import type { OnboardingStepProps } from '@pray/shared/components/onboarding/types';
import AppBar from '@pray/shared/components/ui/AppBar';
import Button from '@pray/shared/components/ui/Button';
import Text from '@pray/shared/components/ui/Text';
import { useAuth } from '@pray/shared/hooks/useAuth';
import { cn } from '@pray/shared/utils/styles';

import { VIEW_EVENT_NAMES } from 'constants.js';

import { HUBSPOT_STEP_VIEW_EVENT_NAMES } from '../../utils/constants';
import { hubspotTrackStepView } from '../../utils/step-tracking';

import styles from './MinistryInfoOnboardingStep.module.scss';

export default function MinistryInfoOnboardingStep({ onContinue, onBack, ...props }: OnboardingStepProps) {
  const { title, subtitle, logo, hasLogo, hasAppBar, showBackButton, showSkipButton } = props;

  const { user } = useAuth();

  // This is a workaround to set the email field value in the Hubspot form when it's a hidden field.
  // This is needed so the Hubspot company is linked with the Hubspot contact.
  const handleFormReady = (event: Event) => {
    const form = window.HubSpotFormsV4.getFormFromEvent(event);
    form.setFieldValue('0-1/email', user.email);
  };

  useEffect(() => {
    hubspotTrackStepView(HUBSPOT_STEP_VIEW_EVENT_NAMES.MINISTRY_INFO);
  }, []);

  useEffect(() => {
    window.addEventListener('hs-form-event:on-ready', handleFormReady);
    return () => {
      window.removeEventListener('hs-form-event:on-ready', handleFormReady);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('hs-form-event:on-submission:success', onContinue);
    return () => {
      window.removeEventListener('hs-form-event:on-submission:success', onContinue);
    };
  }, []);

  const skipButton = <Button onClick={() => onContinue({ isSkipped: true })}>Skip</Button>;

  return (
    <div data-viewname={VIEW_EVENT_NAMES.ONBOARDING_FLOW.MINISTRY_INFO_STEP}>
      {hasAppBar && (
        <AppBar center={hasLogo && logo} right={showSkipButton && skipButton} onBack={showBackButton && onBack} />
      )}
      <div className={cn(styles.container, hasAppBar ? styles.hasAppBar : '')}>
        <div className={styles.body}>
          <div className={cn(styles.headline)}>
            <Text variant={typography.heading_xl} className={styles.title}>
              {title}
            </Text>

            <Text color={colors.text_secondary} className={styles.subtitle}>
              {subtitle}
            </Text>
          </div>
          <HubspotForm
            target="plgMinistryInfoForm"
            hubspotForm={{
              region: 'na1',
              portalId: '2592247',
              formId: '0360a3d2-4e50-424f-9421-0fb8f67580eb',
              onFormSubmitted: onContinue,
            }}
          />
        </div>
      </div>
    </div>
  );
}
